import { toCurrency } from '@/utils/numbers.js';
import { useI18n } from '@/hooks/useI18n.ts';
import { getContentVdpUrl, getReciprocalContentLanguage } from '@/utils/translations.ts';
import type { ContentLanguage } from '@/utils/translations.ts';
import { InventoryUnit } from '@/entities/types/InventoryUnit.ts';

export const decodeVehicleBrochurePlaceholders = (content: string): string => {
    if (!content) {
        return content;
    }

    return content.replaceAll(
        /<vehicle-brochure[^>]*data-html="([^"]+)"[^>]*><\/vehicle-brochure>/gi,
        (_match, encodedHtml) => decodeURIComponent(encodedHtml)
    );
};

export const encodeVehicleBrochurePlaceholders = (content: string): string => {
    if (
        !content ||
        typeof document === 'undefined' ||
        globalThis.window === undefined ||
        globalThis.Node === undefined
    ) {
        return typeof content === 'string' ? content : '';
    }

    const container = document.createElement('div');
    container.innerHTML = content;
    const tables = Array.from(
        container.querySelectorAll('table[data-vehicle-brochure-root="true"]')
    );

    if (!tables.length) {
        return content;
    }

    for (const table of tables) {
        const encodedSegments = [table.outerHTML];
        let sibling = table.nextSibling;

        while (sibling && sibling.nodeType === Node.TEXT_NODE && !sibling.textContent.trim()) {
            sibling = sibling.nextSibling;
        }

        const isSpacerParagraph =
            sibling &&
            sibling.nodeType === Node.ELEMENT_NODE &&
            sibling.tagName.toLowerCase() === 'p' &&
            /<br\s*\/?>/i.test(sibling.innerHTML.trim());

        if (isSpacerParagraph) {
            encodedSegments.push(sibling.outerHTML);
            sibling.remove();
        }

        const placeholder = document.createElement('vehicle-brochure');
        placeholder.dataset.html = encodeURIComponent(encodedSegments.join(''));

        table.replaceWith(placeholder);
    }

    return container.innerHTML;
};

export const useVehicleBrochure = () => {
    const { t, n } = useI18n();

    /**
     * Get the vehicle brochure.
     *
     * Although the html code could largely be optimized in many ways,
     * it must remain as written to ensure maximum compatibility and preserve style consistency across different email clients
     * that have html and css limitations (especially for older versions of Microsoft Outlook for Windows).
     * For that reason, we cannot remove cellspacing and cellpadding obsolete attributes, etc.
     */
    const getVehicleBrochure = (vehicle: InventoryUnit, lang: string): string => {
        return `
            <table
                data-vehicle-brochure="true"
                data-vehicle-brochure-root="true"
                cellspacing="0"
                cellpadding="0"
                style="
                    width: 347px;
                    padding: 0;
                    font-family: system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                    border: 1px solid #D9D9D9;
                    border-spacing: 0;
                    border-radius: 6px;
                    box-shadow: 0 0 5px 2px #D9D9D9;
                "
            >
                ${getTableHead(vehicle, lang)}
                ${getTableBody(vehicle, lang)}
                ${getTableFoot(vehicle, lang)}
            </table>
            <p><br></p>
        `;
    };

    const getYearAndMake = (vehicle: InventoryUnit): string => {
        return `${vehicle.year ?? '---'} ${vehicle.make ?? '---'}`;
    };

    const getModel = (vehicle: InventoryUnit): string => {
        return vehicle.model ?? '---';
    };

    const getPrice = (vehicle: InventoryUnit, lang: string): string => {
        return vehicle.price ? `${toCurrency(vehicle.price, true, false, '', 'CA', lang)}` : '---';
    };

    const getImage = (vehicle: InventoryUnit): string => {
        return (
            vehicle.inventory_unit_image?.url ??
            vehicle.vehicle_image?.image_url_front ??
            'https://res.cloudinary.com/activix/image/upload/v1737653327/dev/image-missing-icon.png'
        );
    };

    const getExteriorAndInteriorColor = (vehicle: InventoryUnit): string => {
        const exterior = vehicle.color_exterior === 'none' ? '---' : vehicle.color_exterior;
        const interior = vehicle.color_interior === 'none' ? '---' : vehicle.color_interior;
        return `${exterior} + ${interior}`;
    };

    const getDrivetrain = (vehicle: InventoryUnit): string => {
        return vehicle.drivetrain === 'none' ? '---' : vehicle.drivetrain;
    };

    const getTransmission = (vehicle: InventoryUnit): string => {
        return vehicle.transmission === 'none' ? '---' : vehicle.transmission;
    };

    const getMileageValue = (vehicle: InventoryUnit): number | null => {
        const sources = [
            vehicle.mileage,
            vehicle.odometer,
            vehicle.mileage_value,
            vehicle.odometer_last_visit,
        ];

        for (const source of sources) {
            if (typeof source === 'number' && Number.isFinite(source)) {
                return source;
            }

            if (typeof source === 'string') {
                const normalized = source.replaceAll(/[^0-9.,-]/g, '').replaceAll(',', '.');
                if (!normalized) {
                    continue;
                }
                const parsed = Number(normalized);
                if (Number.isFinite(parsed)) {
                    return parsed;
                }
            }
        }

        return null;
    };

    const normalizeOdometerUnit = (unit?: string | null): { format: string; label: string } => {
        if (!unit) {
            return { format: 'km', label: 'km' };
        }

        const normalized = unit.trim().toLowerCase();

        if (
            ['km', 'kms', 'kilometer', 'kilometre', 'kilometers', 'kilometres'].includes(normalized)
        ) {
            return { format: 'km', label: 'km' };
        }

        if (['mi', 'mile', 'miles'].includes(normalized)) {
            return { format: 'mi', label: 'mi' };
        }

        return { format: 'decimal', label: unit };
    };

    const tryFormatNumber = (formatFn: () => string): string | null => {
        try {
            return formatFn();
        } catch {
            return null;
        }
    };

    const getOdometer = (vehicle: InventoryUnit, lang: string): string => {
        const { format, label } = normalizeOdometerUnit(vehicle.odometer_unit ?? 'km');
        const mileageValue = getMileageValue(vehicle);
        const locale = lang.toLowerCase();

        if (mileageValue === null) {
            return `--- ${label}`;
        }

        let formatted = tryFormatNumber(() => n(mileageValue, format, locale)) ?? '';

        if (!formatted?.trim()) {
            const fallbackLocale = locale === 'fr' ? 'fr-CA' : 'en-CA';
            const unitMap = {
                km: { style: 'unit', unit: 'kilometer' },
                mi: { style: 'unit', unit: 'mile' },
            } as const;

            const preferredFormatter =
                tryFormatNumber(() =>
                    new Intl.NumberFormat(
                        fallbackLocale,
                        unitMap[format as keyof typeof unitMap] ?? { style: 'decimal' }
                    ).format(mileageValue)
                ) ?? '';

            formatted = preferredFormatter.trim()
                ? preferredFormatter
                : tryFormatNumber(() =>
                      new Intl.NumberFormat(fallbackLocale, { style: 'decimal' }).format(
                          mileageValue
                      )
                  ) ?? mileageValue.toString();
        }

        if (['km', 'mi'].includes(format)) {
            return formatted;
        }

        return `${formatted} ${label}`.trim();
    };

    const getTableHead = (vehicle: InventoryUnit, lang: string): string => {
        return `
            <thead>
                <tr>
                    <td colspan="2" style="padding: 25px 25px 3px 25px;">
                        <table cellspacing="0" cellpadding="0" style="width: 100%; padding: 0; font-family: system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; border-spacing: 0;">
                            <tr>
                                <td style="width: 65%; padding: 0 10px 0 0; font-size: 14px;">${getYearAndMake(
                                    vehicle
                                )}</td>
                                <td style="width: 35%; padding: 0 0 0 10px; font-size: 14px; text-align: right; color: #525252;">${getPrice(
                                    vehicle,
                                    lang
                                )}</td>
                            </tr>
                        </table>
                    </td>
                </tr>
                <tr>
                    <td colspan="2" style="padding: 3px 25px 15px 25px; font-size: 16px;"><b>${getModel(
                        vehicle
                    )}</b></td>
                </tr>
                <tr>
                    <td colspan="2" style="padding: 0 25px 0 25px;">
                        <table cellspacing="0" cellpadding="0" style="width: 100%; height: 1px; padding: 0; border-spacing: 0;">
                            <tr>
                                <td style="height: 1px; padding: 0; background-color: #E1E1E1;"></td>
                            </tr>
                        </table>
                    </td>
                </tr>
            </thead>
        `;
    };

    const getTableBody = (vehicle: InventoryUnit, lang: string): string => {
        return `
            <tbody>
                <tr>
                    <td style="width: 45%; padding: 25px 10px 25px 25px; vertical-align: middle; text-align: left;">
                        <img src="${getImage(
                            vehicle
                        )}" alt="vehicle_image" width="120" style="vertical-align: middle; border-radius: 6px;" />
                    </td>
                    <td style="width: 55%; padding: 25px 25px 25px 10px; vertical-align: middle; text-align: left; font-size: 13px;">
                        <table cellspacing="0" cellpadding="0" style="width: 100%; padding: 0; font-family: system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; border-spacing: 0;">
                            <tr><td style="padding: 3px 0; font-size: 13px;">${getExteriorAndInteriorColor(
                                vehicle
                            )}</td></tr>
                            <tr><td style="padding: 3px 0; font-size: 13px;">${getDrivetrain(
                                vehicle
                            )}</td></tr>
                            <tr><td style="padding: 3px 0; font-size: 13px;">${getTransmission(
                                vehicle
                            )}</td></tr>
                            <tr><td style="padding: 3px 0; font-size: 13px;">${getOdometer(
                                vehicle,
                                lang
                            )}</td></tr>
                        </table>
                    </td>
                </tr>
            </tbody>
        `;
    };

    /**
     * Get the vehicle brochure footer with VDP button if the url is available.
     *
     * The vml code section (<!--[if mso]>) must remain to ensure compatibility with older versions of Microsoft Outlook for Windows.
     */
    const getTableFoot = (vehicle: InventoryUnit, lang: string): string => {
        const language = lang.toLowerCase() as ContentLanguage;
        const url = getContentVdpUrl(vehicle, language, getReciprocalContentLanguage(language));

        if (url) {
            return `
                <tfoot>
                    <tr>
                        <td colspan="2" style="padding: 0 25px 25px 25px; vertical-align: middle; text-align: center;">
                            <!--[if mso]><v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${url}" style="height: 36px; v-text-anchor: middle; width: 295px;" arcsize="9%" stroke="f" fillcolor="#3E8DBC"><w:anchorlock/><center style="font-family: system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;"><![endif]-->
                            <a href="${url}" target="_blank" style="display: inline-block; width: 100%; padding: 0; line-height: 36px; font-size: 14px; color:#FFFFFF; background-color: #3E8DBC; text-decoration: none; border-radius: 3px; box-shadow: 0 0 5px 2px #D9D9D9;">${getButtonText(
                lang
            )}</a>
                            <!--[if mso]></center></v:roundrect><![endif]-->
                        </td>
                    </tr>
                </tfoot>
            `;
        }
        return '';
    };

    const getButtonText = (lang: string): string => {
        return t('responseLeadXpress.viewDetails', [], lang.toLowerCase());
    };

    const minify = (content: string): string => {
        return content
            .replaceAll(/\s{2,}/g, ' ')
            .replaceAll('\n', ' ')
            .trim();
    };

    return {
        getVehicleBrochure,
        minify,
        decodeVehicleBrochurePlaceholders,
        encodeVehicleBrochurePlaceholders,
    };
};
